import { Preferences } from '@capacitor/preferences';

const SESSION_EXPIRY_DAYS = 120;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STORAGE_KEY = 'dealsaver_login_timestamp';

/**
 * Store the current login timestamp in Capacitor Preferences.
 * Called when user successfully logs in.
 */
export async function setLoginTime(): Promise<void> {
  try {
    const now = new Date().toISOString();
    await Preferences.set({
      key: STORAGE_KEY,
      value: now,
    });
  } catch (error) {
    console.warn('Failed to store login timestamp:', error);
    // Continue without timestamp - graceful degradation
  }
}

/**
 * Clear the stored login timestamp from Capacitor Preferences.
 * Called when user logs out.
 */
export async function clearSession(): Promise<void> {
  try {
    await Preferences.remove({ key: STORAGE_KEY });
  } catch (error) {
    console.warn('Failed to clear session storage:', error);
  }
}

/**
 * Check if session exists and is still valid (not expired).
 * Returns true if timestamp doesn't exist (fail-safe for legacy users).
 * Returns false if timestamp exists but is >= 120 days old.
 */
export async function isSessionValid(): Promise<boolean> {
  try {
    const { value: storedTimestamp } = await Preferences.get({ key: STORAGE_KEY });

    // No timestamp = treat as valid (don't force logout legacy users)
    if (!storedTimestamp) {
      return true;
    }

    // Validate ISO format before parsing
    if (!/^\d{4}-\d{2}-\d{2}T/.test(storedTimestamp)) {
      console.warn('Invalid timestamp format in storage');
      return true; // Fail-safe
    }

    const loginTime = new Date(storedTimestamp).getTime();
    const now = new Date().getTime();
    const ageInDays = (now - loginTime) / MS_PER_DAY;

    return ageInDays < SESSION_EXPIRY_DAYS;
  } catch (error) {
    console.warn('Failed to validate session:', error);
    return true; // Fail-safe
  }
}

/**
 * Check if session has expired.
 * If expired: show alert message and force logout.
 * Returns true if session is valid, false if expired.
 */
export async function checkExpiry(): Promise<boolean> {
  try {
    const isValid = await isSessionValid();

    if (!isValid) {
      // Session expired - show alert and logout
      alert('Your session has expired. Please log in again.');
      // Call logout without the dialog (we already showed the alert)
      await handleLogoutSilent();
      return false;
    }

    return true;
  } catch (error) {
    console.error('Session check failed:', error);
    // Continue with caution on error
    return true;
  }
}

/**
 * Helper: logout without showing the confirmation dialog.
 * Called internally when session expires.
 */
async function handleLogoutSilent(): Promise<void> {
  const { signOutUser } = await import('../firebase');
  const { f7 } = await import('framework7-react');

  try {
    await signOutUser();
    await clearSession();
    f7.views.main.router.navigate('/', { reloadAll: true });
  } catch (error) {
    console.error('Failed to logout on session expiry:', error);
    f7.dialog.alert('Failed to log out. Please try again.');
  }
}
